import { minBy } from 'lodash-es';
import type { Airflow } from '@aviata/platform-types';

import type { SortMode } from '@/features/offer-sort';

import type { amplitudeEventsConfigTypes } from '@/shared/config';

import { amplitudeEventsLib } from '../amplitude-events';
import { googleAnalyticsEventsLib } from '../google-analytics-events';
import { jsBridgeEventsLib } from '../js-bridge';
import { bloomreachEventsLib } from '../bloomreach-events';
import { acceleraEventsLib } from '../accelera-events';
import type { SearchResultCardCashbackPriceClickParams } from './types';

export type SearchQueryForJSBridge = {
  cityFrom?: string;
  airportFrom?: string;
  cityTo?: string;
  airportTo?: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  cabinClass?: string;
  passengerAdult?: number;
  passengerYouth?: number;
  passengerChild?: number;
  passengerTotal?: number;
};

export type SearchPageOpenParams = {
  success: boolean;
  error?: string;
  searchTimeMs: number;
  partnerFilter?: boolean;
  segmentFilter?: boolean;
  filterParams?: Record<string, unknown>;
  offers?: Airflow.Offer.Entity[];
  calendarFilter?: boolean;
  source?: amplitudeEventsConfigTypes.SERPOpenSource;
  /** Authenticated user's phone, sourced from the account request (used by Accelera). */
  phone?: string;
  // Additional data for JS Bridge
  searchQuery?: SearchQueryForJSBridge;
  // Additional data for Bloomreach
  dict?: Record<string, unknown>;
  searchParams?: {
    dep?: string;
    arr?: string;
    itineraries?: Array<{ date: string; dep?: string; arr?: string }>;
    passengers?: {
      adt?: number;
      yth?: number;
      chd?: number;
    };
    cabinClass?: string;
  };
};

export const searchPageOpen = (params: SearchPageOpenParams) => {
  const minPrice =
    Number(minBy(params.offers, o => Number(o.price.amount))?.price.amount) ||
    undefined;

  googleAnalyticsEventsLib.searchPageOpen();
  amplitudeEventsLib.searchPageOpen(params);
  acceleraEventsLib.serpPageOpened(params);
  jsBridgeEventsLib.serpPageOpened(params);
  bloomreachEventsLib.searchPageOpen({ ...params, minPrice });
};

export const priceCalendarClick = () => {
  amplitudeEventsLib.priceCalendarClick();
};

export const filtersClick = () => {
  amplitudeEventsLib.filtersClick();
};

export const resetFiltersClick = (source: string) => {
  amplitudeEventsLib.resetFiltersClick(source);
};

export const applyFiltersClick = (appliedFilters?: Record<string, unknown>) => {
  amplitudeEventsLib.applyFiltersClick(appliedFilters);
};

export const sortSeparateClick = () => {
  amplitudeEventsLib.sortSeparateClick();
};

export const airlinesSeparateClick = () => {
  amplitudeEventsLib.airlinesSeparateClick();
};

export const timeSeparateClick = () => {
  amplitudeEventsLib.timeSeparateClick();
};

export const transfersSeparateClick = () => {
  amplitudeEventsLib.transfersSeparateClick();
};

export const airlinesSeparateApplied = (params: {
  airline_choosed: string;
}) => {
  amplitudeEventsLib.airlinesSeparateApplied(params);
};

export const timeSeparateApplied = (params: { travel_time: string }) => {
  amplitudeEventsLib.timeSeparateApplied(params);
};

export const transfersSeparateApplied = (params: {
  transfers: string;
  no_night_transfer: string;
}) => {
  amplitudeEventsLib.transfersSeparateApplied(params);
};

export const directSeparateClick = () => {
  amplitudeEventsLib.directSeparateClick();
};

export const directSeparateApplied = (params: { is_direct: string }) => {
  amplitudeEventsLib.directSeparateApplied(params);
};

export const changeSortMode = ({ sortMode }: { sortMode: SortMode }) => {
  amplitudeEventsLib.changeSortMode({ sortMode });
};

export const directFlightsView = () => {
  amplitudeEventsLib.directFlightsView();
};

export const directFlightsClick = (params: {
  airline_choosen: string;
  time_choosen?: string;
}) => {
  amplitudeEventsLib.directFlightsClick(params);
};

export const directFlightsShowMore = () => {
  amplitudeEventsLib.directFlightsShowMore();
};

export const openChangeSearchParams = () => {
  amplitudeEventsLib.openChangeSearchParams();
};

export const priceCalendarShow = () => {
  amplitudeEventsLib.priceCalendarShow();
};

export const priceSwipeRight = (direction: 'from' | 'to') => {
  amplitudeEventsLib.priceSwipeRight(direction);
};

export const priceSwipeLeft = (direction: 'from' | 'to') => {
  amplitudeEventsLib.priceSwipeLeft(direction);
};

export const priceCalendarClose = () => {
  amplitudeEventsLib.priceCalendarClose();
};

export const priceCalendarOffersShow = () => {
  amplitudeEventsLib.priceCalendarOffersShow();
};

export const serpScroll = (params: {
  scroll_depth?: number;
  scroll_offers?: number;
}) => {
  amplitudeEventsLib.serpScroll(params);
};

export const promocodeApplied = (params: { value: string }) => {
  amplitudeEventsLib.promocodeApplied(params);
};

export const searchResultCardCashbackPriceClick = (
  params: SearchResultCardCashbackPriceClickParams,
) => {
  amplitudeEventsLib.searchResultCardCashbackPriceClick(params);
};
